import { execFile } from "node:child_process";
import { realpathSync } from "node:fs";
import { promisify } from "node:util";

// EVERY git call in this file is async (`execFile`, no shell). `execSync`
// freezes the single Node event loop, which starves the agent's socket.io
// heartbeat — the API's stall detection then restarts the session, tearing
// down whatever the agent was doing. This is not hypothetical: the periodic
// WIP flush used to run `git status`/`git add -A`/`git stash create`
// synchronously with no timeout, and on an IO-starved pod (a heavy test gate
// hammering the same disk) a single tick blocked the loop for 40+ minutes —
// the execution log went silent and the socket reconnected on attempt 1 the
// moment the stuck call finally returned. Awaiting a child process instead
// keeps the loop free to answer pings while git grinds.
//
// Rules for this file:
//   - no execSync, ever;
//   - every call has a timeout (a hung git can't wedge a flush tick forever);
//   - args are passed as arrays (no shell quoting surprises).
const execFileAsync = promisify(execFile);

const GIT_TIMEOUT_MS = 60_000;
// Staging/stashing a large dirty tree is legitimately slow on a loaded pod —
// give the IO-heavy calls more headroom before declaring them hung.
const GIT_SLOW_TIMEOUT_MS = 120_000;
// `git status -z` output on a very dirty tree can exceed the 1MB default.
const GIT_MAX_BUFFER = 16 * 1024 * 1024;

/** Run git with args (no shell), bounded by a timeout. Returns trimmed stdout.
 *  Throws on non-zero exit or timeout — callers decide the failure policy. */
async function git(cwd: string, args: string[], timeoutMs = GIT_TIMEOUT_MS): Promise<string> {
  const { stdout } = await execFileAsync("git", args, {
    cwd,
    timeout: timeoutMs,
    maxBuffer: GIT_MAX_BUFFER,
  });
  return stdout.toString().trim();
}

/** Fetch and merge the latest base branch to prevent stale-base merge conflicts.
 *  Returns true on success or no-op (empty baseBranch), false on failure. */
export async function syncWithBaseBranch(cwd: string, baseBranch: string): Promise<boolean> {
  if (!baseBranch) return true;

  try {
    await git(cwd, ["fetch", "origin", baseBranch]);
  } catch {
    process.stderr.write(
      `[conveyor-agent] Warning: git fetch origin ${baseBranch} failed, continuing with current base\n`,
    );
    return false;
  }

  try {
    await git(cwd, ["merge", `origin/${baseBranch}`, "--no-edit"], 30_000);
  } catch {
    process.stderr.write(
      `[conveyor-agent] Warning: merge origin/${baseBranch} failed, aborting merge and continuing\n`,
    );
    try {
      await git(cwd, ["merge", "--abort"], 30_000);
    } catch {
      // merge --abort can fail if there's nothing to abort
    }
    return false;
  }

  process.stderr.write(`[conveyor-agent] Synced with latest origin/${baseBranch}\n`);
  return true;
}

/** Ensure the repo is on the expected task branch. No-op if already correct.
 *  Returns true on success, false on failure. */
export async function ensureOnTaskBranch(cwd: string, taskBranch: string): Promise<boolean> {
  if (!taskBranch) return true;

  const current = await getCurrentBranch(cwd);
  if (current === taskBranch) return true;

  try {
    await git(cwd, ["fetch", "origin", taskBranch]);
  } catch {
    process.stderr.write(`[conveyor-agent] Warning: git fetch origin ${taskBranch} failed\n`);
    return false;
  }

  // Use `checkout -B` which creates or resets the local branch to track origin.
  // This handles the detached-HEAD case (entrypoint.sh leaves the repo detached
  // after `git reset --hard origin/<branch>`) and the case where a stale local
  // branch exists pointing at a different SHA.
  try {
    await git(cwd, ["checkout", "-B", taskBranch, `origin/${taskBranch}`], 30_000);
  } catch {
    process.stderr.write(`[conveyor-agent] Warning: git checkout ${taskBranch} failed\n`);
    return false;
  }

  process.stderr.write(`[conveyor-agent] Checked out task branch ${taskBranch}\n`);
  return true;
}

/** Returns true if the working tree has uncommitted changes (staged or unstaged). */
export async function hasUncommittedChanges(cwd: string): Promise<boolean> {
  const status = await git(cwd, ["status", "--porcelain"], GIT_SLOW_TIMEOUT_MS);
  return status.length > 0;
}

/** Returns the current branch name, or null if on detached HEAD. */
export async function getCurrentBranch(cwd: string): Promise<string | null> {
  try {
    const branch = await git(cwd, ["branch", "--show-current"]);
    return branch || null;
  } catch {
    return null;
  }
}

/** Returns true if there are committed changes that haven't been pushed to origin. */
export async function hasUnpushedCommits(cwd: string): Promise<boolean> {
  try {
    const currentBranch = await getCurrentBranch(cwd);
    if (!currentBranch) return false;

    try {
      await git(cwd, ["rev-parse", `origin/${currentBranch}`]);
    } catch {
      try {
        await git(cwd, ["rev-parse", "HEAD"]);
        return true;
      } catch {
        return false;
      }
    }

    const ahead = await git(cwd, [
      "rev-list",
      "--count",
      "HEAD",
      "--not",
      `origin/${currentBranch}`,
    ]);
    return parseInt(ahead, 10) > 0;
  } catch {
    return false;
  }
}

/** Stage all changes and create a commit. Returns the commit hash if successful. */
export async function stageAndCommit(cwd: string, message: string): Promise<string | null> {
  try {
    await git(cwd, ["add", "-A"], GIT_SLOW_TIMEOUT_MS);

    if (!(await hasUncommittedChanges(cwd))) return null;

    await git(cwd, ["commit", "-m", message], GIT_SLOW_TIMEOUT_MS);

    return await git(cwd, ["rev-parse", "HEAD"]);
  } catch {
    return null;
  }
}

async function tryPush(cwd: string, branch: string, skipVerify = false): Promise<boolean> {
  const noVerify = skipVerify ? ["--no-verify"] : [];
  try {
    await git(cwd, ["push", ...noVerify, "origin", branch], 30_000);
    return true;
  } catch {
    try {
      await git(cwd, ["push", ...noVerify, "--force-with-lease", "origin", branch], 30_000);
      return true;
    } catch {
      return false;
    }
  }
}

async function isAuthError(cwd: string): Promise<boolean> {
  try {
    await git(cwd, ["push", "--dry-run"], 30_000);
    return false;
  } catch (err: unknown) {
    if ((err as { killed?: boolean }).killed) return true;
    const stderr = (err as { stderr?: Buffer | string }).stderr?.toString() ?? "";
    const stdout = (err as { stdout?: Buffer | string }).stdout?.toString() ?? "";
    const msg = stderr || stdout || (err instanceof Error ? err.message : "");
    return /authentication|authorization|403|401|token/i.test(msg);
  }
}

/** Update the git remote URL with a fresh token. */
export async function updateRemoteToken(cwd: string, token: string): Promise<void> {
  try {
    const url = await git(cwd, ["remote", "get-url", "origin"]);
    const match = url.match(/github\.com[/:]([^/]+\/[^/.]+)/);
    if (match) {
      const repo = match[1].replace(/\.git$/, "");
      await git(cwd, [
        "remote",
        "set-url",
        "origin",
        `https://x-access-token:${token}@github.com/${repo}.git`,
      ]);
    }
  } catch {
    // silently fail — push retry will surface the real error
  }
}

// ── WIP snapshot ref ────────────────────────────────────────────────────
//
// Crash protection without polluting the task branch: uncommitted work is
// captured as a stash-shaped commit (`git stash create` — does not touch
// HEAD, the index, or the working tree) and force-pushed to a dedicated
// `conveyor-wip/<branch>` ref. Real (intentional) commits are still pushed
// to the task branch itself. On a fresh pod, `restoreWipSnapshot` re-applies
// the snapshot when the branch head matches; once the tree is clean the
// stale remote ref is deleted.

/** The remote ref that holds the WIP snapshot for a task branch. */
export function wipRefForBranch(branch: string): string {
  return `conveyor-wip/${branch}`;
}

/** cwd values that have (or may have) a live remote WIP ref to clean up. */
const wipRefPushed = new Set<string>();

/**
 * Capture the working tree + index (including untracked files) as a
 * stash-shaped commit WITHOUT moving HEAD or dirtying history. The index is
 * staged so untracked files are included, then restored so the agent's
 * `git status` view is unchanged.
 */
async function createWipSnapshot(cwd: string, message: string): Promise<string | null> {
  try {
    await git(cwd, ["add", "-A"], GIT_SLOW_TIMEOUT_MS);
    const sha = await git(cwd, ["stash", "create", message], GIT_SLOW_TIMEOUT_MS);
    return sha || null;
  } catch {
    return null;
  } finally {
    try {
      await git(cwd, ["reset", "-q"], GIT_SLOW_TIMEOUT_MS);
    } catch {
      /* nothing staged */
    }
  }
}

async function tryPushRefspec(cwd: string, refspec: string, force = false): Promise<boolean> {
  try {
    // --no-verify: a WIP snapshot to a throwaway conveyor-wip/* ref is a machine
    // crash-protection push, not a real branch update — it must never run the
    // pre-push quality gate (lint/typecheck/test).
    const forceArgs = force ? ["--force"] : [];
    await git(cwd, ["push", "--no-verify", ...forceArgs, "origin", refspec], 30_000);
    return true;
  } catch {
    return false;
  }
}

async function refreshRemoteToken(
  cwd: string,
  refreshToken?: () => Promise<string | undefined>,
): Promise<void> {
  if (!refreshToken) return;
  try {
    const token = await refreshToken();
    if (token) {
      await updateRemoteToken(cwd, token);
      process.env.GITHUB_TOKEN = token;
      process.env.GH_TOKEN = token;
    }
  } catch {
    // best effort — proceed with existing token
  }
}

/**
 * Re-apply a WIP snapshot pushed by a previous pod of this task. When the
 * snapshot was taken on the current HEAD, this is a plain re-apply. When the
 * branch advanced past the snapshot (review commits, a dev merge, another
 * pod), the snapshot is 3-way applied onto the new head so newer remote work
 * AND the WIP both land. Only a genuine line-level conflict refuses
 * ("stale") — the wip ref is kept on origin in that case so nothing is lost:
 *   git fetch origin refs/heads/conveyor-wip/<branch> && git stash apply FETCH_HEAD
 */
export async function restoreWipSnapshot(
  cwd: string,
  branch: string,
): Promise<"applied" | "stale" | "none" | "failed"> {
  if (!branch) return "none";
  const ref = wipRefForBranch(branch);
  try {
    await git(cwd, ["fetch", "origin", `+refs/heads/${ref}:refs/remotes/origin/${ref}`]);
  } catch {
    // Ref doesn't exist remotely (or fetch failed) — nothing to restore.
    return "none";
  }
  // A remote ref exists — remember to clean it up once the tree is clean.
  wipRefPushed.add(cwd);
  try {
    const sha = await git(cwd, ["rev-parse", `refs/remotes/origin/${ref}`]);
    // First parent of a stash-shaped commit is the HEAD it was created on.
    const parent = await git(cwd, ["rev-parse", `${sha}^`]);
    const head = await git(cwd, ["rev-parse", "HEAD"]);
    if (parent !== head) {
      // Branch moved since the snapshot. Try the 3-way apply (the stash
      // commit's first parent is the merge base, so git can transplant the
      // WIP onto the new head); back out cleanly on conflict and keep the
      // origin ref for manual recovery.
      try {
        await git(cwd, ["stash", "apply", sha], GIT_SLOW_TIMEOUT_MS);
        return "applied";
      } catch {
        try {
          await git(cwd, ["reset", "--merge"], GIT_SLOW_TIMEOUT_MS);
        } catch {
          // best effort — leave whatever git managed to clean up
        }
        return "stale";
      }
    }
    await git(cwd, ["stash", "apply", sha], GIT_SLOW_TIMEOUT_MS);
    return "applied";
  } catch {
    return "failed";
  }
}

/** Best-effort flush of pending work: pushes any real (intentional) commits to
 *  the task branch, and captures uncommitted changes as a WIP snapshot on the
 *  `conveyor-wip/<branch>` ref — never as commits on the branch itself. When
 *  the tree is clean, a previously pushed WIP ref is deleted. Never throws. */
export async function flushPendingChanges(
  cwd: string,
  opts?: { wipMessage?: string; refreshToken?: () => Promise<string | undefined> },
): Promise<{ committed: boolean; pushed: boolean; hadWork: boolean }> {
  let committed = false;
  let pushed = false;
  let hadWork = false;

  try {
    const branch = await getCurrentBranch(cwd);
    if (!branch) return { committed, pushed, hadWork };

    const dirty = await hasUncommittedChanges(cwd);
    const unpushed = await hasUnpushedCommits(cwd);

    if (!dirty && !unpushed) {
      await dropStaleWipRef(cwd, branch, opts?.refreshToken);
      return { committed, pushed, hadWork };
    }

    hadWork = true;
    await refreshRemoteToken(cwd, opts?.refreshToken);

    // Real commits first so a WIP snapshot taken below sits on the pushed head.
    if (unpushed) {
      pushed = await pushToOrigin(cwd, opts?.refreshToken);
    }

    if (dirty) {
      const message = opts?.wipMessage ?? "WIP: conveyor-agent snapshot";
      const sha = await createWipSnapshot(cwd, message);
      if (sha) {
        committed = await tryPushRefspec(cwd, `${sha}:refs/heads/${wipRefForBranch(branch)}`, true);
        if (committed) wipRefPushed.add(cwd);
      }
    }
  } catch {
    // best effort
  }

  return { committed, pushed, hadWork };
}

/** Clean tree: the last WIP snapshot is stale — drop the remote ref so
 *  recovery never resurrects superseded work. */
async function dropStaleWipRef(
  cwd: string,
  branch: string,
  refreshToken?: () => Promise<string | undefined>,
): Promise<void> {
  if (!wipRefPushed.has(cwd)) return;
  await refreshRemoteToken(cwd, refreshToken);
  if (await tryPushRefspec(cwd, `:refs/heads/${wipRefForBranch(branch)}`)) {
    wipRefPushed.delete(cwd);
  }
}

/** Push current branch to origin. Proactively refreshes the GitHub token before
 *  attempting the push. On auth failure, refreshes again and retries. */
export async function pushToOrigin(
  cwd: string,
  refreshToken?: () => Promise<string | undefined>,
  skipVerify = false,
): Promise<boolean> {
  try {
    const currentBranch = await getCurrentBranch(cwd);
    if (!currentBranch) return false;

    // Proactively refresh token before pushing to avoid wasting time with a stale token
    if (refreshToken) {
      try {
        const token = await refreshToken();
        if (token) {
          await updateRemoteToken(cwd, token);
          process.env.GITHUB_TOKEN = token;
          process.env.GH_TOKEN = token;
        }
      } catch {
        // best effort — proceed with existing token
      }
    }

    if (await tryPush(cwd, currentBranch, skipVerify)) return true;

    if (refreshToken && (await isAuthError(cwd))) {
      const token = await refreshToken();
      if (token) {
        await updateRemoteToken(cwd, token);
        process.env.GITHUB_TOKEN = token;
        process.env.GH_TOKEN = token;
        return await tryPush(cwd, currentBranch, skipVerify);
      }
    }

    return false;
  } catch {
    return false;
  }
}

// ── Whole-repo crash protection ─────────────────────────────────────────
//
// `flushPendingChanges` only protects the primary workspace's current branch.
// A pod that dies while a 5-subagent build has produced several unpushed
// feature branches (and/or dirty per-subagent worktrees) loses all of it — the
// pod is reprovisioned as a fresh clone of the branch tip on GitHub (see runbook
// claudespace-pod-deaths-unpushed-work-loss). `flushAllPendingWork` widens the
// net to EVERY local branch and worktree, backing them up under the throwaway
// `conveyor-wip/*` namespace (never real branch names, so origin isn't polluted
// with half-baked branches and CI/PRs aren't triggered).

/** Backup ref that mirrors a local branch's tip (committed, unpushed work). */
export function branchBackupRef(branch: string): string {
  return `conveyor-wip/branches/${branch}`;
}

/** Enumerate the repo's worktrees as { path, branch }. branch is null for a
 *  detached HEAD. Returns [] on any failure. */
async function listWorktrees(cwd: string): Promise<{ path: string; branch: string | null }[]> {
  try {
    const out = await git(cwd, ["worktree", "list", "--porcelain"]);
    const result: { path: string; branch: string | null }[] = [];
    for (const entry of out.split("\n\n")) {
      const lines = entry.trim().split("\n");
      const wl = lines.find((l) => l.startsWith("worktree "));
      if (!wl) continue;
      const bl = lines.find((l) => l.startsWith("branch "));
      result.push({
        path: wl.slice("worktree ".length),
        branch: bl ? bl.slice("branch refs/heads/".length) : null,
      });
    }
    return result;
  } catch {
    return [];
  }
}

/** All local branch short-names (refs/heads/*). Returns [] on failure. */
async function listLocalBranches(cwd: string): Promise<string[]> {
  try {
    const out = await git(cwd, ["for-each-ref", "--format=%(refname:short)", "refs/heads/"]);
    return out
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/** Count of commits on `branch` not reachable from any origin/* ref — i.e.
 *  genuinely unpushed local work. 0 covers both "already pushed" and "an exact
 *  copy of a remote branch (e.g. base)". */
async function branchUnpushedCount(cwd: string, branch: string): Promise<number> {
  try {
    const n = await git(cwd, ["rev-list", "--count", branch, "--not", "--remotes=origin"]);
    return Number.parseInt(n, 10) || 0;
  } catch {
    return 0;
  }
}

function samePath(a: string, b: string): boolean {
  try {
    return realpathSync(a) === realpathSync(b);
  } catch {
    return a === b;
  }
}

/** Best-effort crash protection for the WHOLE repo, not just the primary
 *  branch:
 *   1. the primary workspace via `flushPendingChanges` (real commits → task
 *      branch, dirty tree → conveyor-wip/<branch>) — unchanged behavior;
 *   2. dirty working trees in every OTHER worktree → a stash-shaped snapshot on
 *      conveyor-wip/<worktree-branch>;
 *   3. committed-but-unpushed work on every OTHER local branch → its tip mirrored
 *      to conveyor-wip/branches/<branch>.
 *  Each unit is failure-isolated so one bad branch/worktree can't abort the rest.
 *  Never throws. */
export async function flushAllPendingWork(
  cwd: string,
  opts?: { wipMessage?: string; refreshToken?: () => Promise<string | undefined> },
): Promise<{ hadWork: boolean; branchesBackedUp: number; worktreesSnapshotted: number }> {
  try {
    // 1. Primary workspace — existing behavior (real push + current-branch WIP).
    const primary = await flushPendingChanges(cwd, opts);
    await refreshRemoteToken(cwd, opts?.refreshToken);
    const currentBranch = await getCurrentBranch(cwd);

    // 2 & 3. Widen to the other worktrees and branches.
    const worktreesSnapshotted = await snapshotOtherWorktrees(cwd, opts?.wipMessage);
    const branchesBackedUp = await backupOtherBranches(cwd, currentBranch);

    return {
      hadWork: primary.hadWork || worktreesSnapshotted > 0 || branchesBackedUp > 0,
      branchesBackedUp,
      worktreesSnapshotted,
    };
  } catch {
    return { hadWork: false, branchesBackedUp: 0, worktreesSnapshotted: 0 };
  }
}

/** Snapshot dirty working trees in every worktree except the primary `cwd` to
 *  conveyor-wip/<worktree-branch>. Returns how many were snapshotted. */
async function snapshotOtherWorktrees(cwd: string, wipMessage?: string): Promise<number> {
  let count = 0;
  for (const wt of await listWorktrees(cwd)) {
    if (samePath(wt.path, cwd) || !wt.branch) continue;
    try {
      if (!(await hasUncommittedChanges(wt.path))) continue;
      const sha = await createWipSnapshot(wt.path, wipMessage ?? "WIP: conveyor-agent snapshot");
      if (
        sha &&
        (await tryPushRefspec(wt.path, `${sha}:refs/heads/${wipRefForBranch(wt.branch)}`, true))
      ) {
        wipRefPushed.add(wt.path);
        count++;
      }
    } catch {
      // isolate per-worktree failure
    }
  }
  return count;
}

/** Mirror committed-but-unpushed work on every local branch (except the primary
 *  workspace's current branch and our own conveyor-wip/* refs) to
 *  conveyor-wip/branches/<branch>. Returns how many were backed up. */
async function backupOtherBranches(cwd: string, currentBranch: string | null): Promise<number> {
  let count = 0;
  for (const branch of await listLocalBranches(cwd)) {
    // The current branch is handled by step 1; never back up our own snapshots.
    if (branch === currentBranch || branch.startsWith("conveyor-wip/")) continue;
    try {
      if ((await branchUnpushedCount(cwd, branch)) === 0) continue;
      if (
        await tryPushRefspec(
          cwd,
          `refs/heads/${branch}:refs/heads/${branchBackupRef(branch)}`,
          true,
        )
      ) {
        count++;
      }
    } catch {
      // isolate per-branch failure
    }
  }
  return count;
}
